/* eslint-disable @typescript-eslint/no-explicit-any*/
import { useCallback, useEffect, useRef, useState } from 'react'

export const errorMessageHandler = (error: any, options?: { message?: string; logAll?: boolean }) => {
  const isAbortError = error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')
  if (!isAbortError || options?.logAll) console.log(options?.message ?? 'Error fetching data:', error)
  return { isAbortError }
}

export type FetcherFunction<T> = (signal?: AbortSignal) => Promise<T>

export function useFetchData<T, F extends FetcherFunction<T>>(fetcher: F) {
  type DataType = Awaited<ReturnType<F>>
  const [data, setData] = useState<DataType>()
  const [loading, setLoading] = useState<boolean>(true)
  const abortController = useRef<AbortController | null>(null)

  const fetchTableData = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true)
        const res = await fetcher(signal)
        setData(res as DataType)
        setLoading(false)
      } catch (error) {
        const { isAbortError } = errorMessageHandler(error)
        if (!isAbortError) setLoading(false)
      }
    },
    [fetcher]
  )

  const refresh = useCallback(() => {
    const current = abortController.current
    if (current && !current.signal.aborted) {
      current.abort()
    }

    const newController = new AbortController()
    abortController.current = newController
    fetchTableData(newController.signal)
  }, [fetchTableData])

  useEffect(() => {
    refresh()
    return () => {
      const cur = abortController.current
      if (cur) cur.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { data, loading, refresh }
}
